// ═══════════════════════════════════════════════════════════════════
// FieldChrome3D.js — SHIP 6: THE CHROME
// ═══════════════════════════════════════════════════════════════════
// The 2D Shatterfield's HUD, lifted whole and floated over the 3D
// board. Crystalline Anima heart, Khao vortex, ornate name plates and
// framed turn timers — the same shapes, the same palette, the same
// flash-on-change behaviour as <PlayerSide>.
//
// THE CONTRACT:
//   • PURE DOM, PURE CHROME. Zero three.js, zero <Canvas>, zero game
//     logic, and — since the July 25 no-redundancy ruling — zero click
//     targets. It reads `me` / `opponent` and draws. It cannot break the
//     duel and it cannot steal a click from the board beneath it.
//   • Layer 7. It renders OUTSIDE the Canvas, above the CSS3D face
//     layer (z 5) and below Shatterfield.js's own chrome (z 30) — so
//     radials, modals, heralds and toasts still win, exactly as they
//     do over the 2D board.
//   • Every keyframe it needs that Shatterfield.js does NOT already
//     inject is injected here with a SELF-HEALING guard (replace the
//     node when the text differs — an id-only guard serves a previous
//     build's CSS forever and the new animation silently never runs).
//
// PALETTE LAW: GM = chrome. SD = the field's own colours (Anima red,
// Khao gold, the violet void).
// ═══════════════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from "react";
import { SD } from "./theme";

// ⚠ PALETTE NOTE (flagged, not silent): the duel screen runs a WARM
// gold-on-brown chrome palette that Shatterfield.js declares locally —
// it is NOT theme.js's GM, which is the app-wide cold violet-glass set.
// Importing theme's GM here would have made every plate read violet
// while the 2D board's read brown: the exact drift caP'n is asking to
// close. So the duel palette is mirrored here verbatim.
// TODO (one-source-of-truth): promote this to `export const GM_DUEL` in
// theme.js and have BOTH Shatterfield.js and this file import it. That
// edit is a two-line change in each — deliberately deferred so this
// ship doesn't require re-cutting the 10k-line parent.
const GM = {
  page:          "#0f0c07",
  pageWarm:      "#1a1509",
  pageMid:       "#241c0c",
  pageLight:     "#2e2410",
  pageBorder:    "#3d2e14",
  inkGold:       "#c8963c",
  inkGoldBright: "#e8b84b",
  inkGoldDim:    "#8a6520",
  inkBrown:      "#8a6a30",
  inkRed:        "#8b2020",
  vellum:        "#e8d5a3",
  vellumDim:     "#b89a5a",
  vellumFaint:   "#7a6040",
  ruling:        "rgba(139,106,48,0.18)",
  font: "'Cinzel', 'Palatino Linotype', serif",
};

const isMobile = typeof window !== "undefined" && window.innerWidth < 480;

const formatTime = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
};

// ── Keyframes ──────────────────────────────────────────────────────
// Named sf3dc-* so they can never collide with Shatterfield.js's own
// sheet. sf-khaoSpin / sf-hp-flash / sf-khao-flash are NOT redefined —
// those already ship in the parent's <style> block, which is mounted in
// both 2D and 3D mode. Redefining them here would be a second source of
// truth for the same animation.
const CHROME_CSS = `
@keyframes sf3dc-dangerVeil {
  0%,100% { opacity: 0.30; }
  50%     { opacity: 0.62; }
}
@keyframes sf3dc-hitFlash {
  0%   { opacity: 0.55; }
  100% { opacity: 0; }
}
@keyframes sf3dc-healFlash {
  0%   { opacity: 0.32; }
  100% { opacity: 0; }
}
@keyframes sf3dc-plateRise {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

function ensureChromeKeyframes() {
  if (typeof document === "undefined") return;
  const id = "sf3d-chrome-keys";
  let el = document.getElementById(id);
  // STALE-<style> SELF-HEAL — an id-only guard keeps serving the last
  // build's CSS after an in-place update, and a renamed keyframe then
  // presents as "the effect just doesn't happen."
  if (el && el.textContent === CHROME_CSS) return;
  if (el) el.remove();
  el = document.createElement("style");
  el.id = id;
  el.textContent = CHROME_CSS;
  document.head.appendChild(el);
}

// ── useFlashOnChange — the 2D hook, verbatim ───────────────────────
function useFlashOnChange(value, { duration = 600, direction = false } = {}) {
  const [flash, setFlash] = useState(null); // null | "up" | "down" | "change"
  const prevRef = useRef(value);
  useEffect(() => {
    if (prevRef.current === value) return undefined;
    if (direction) {
      const prev = Number(prevRef.current);
      const next = Number(value);
      if (!Number.isNaN(prev) && !Number.isNaN(next)) setFlash(next > prev ? "up" : "down");
      else setFlash("change");
    } else {
      setFlash("change");
    }
    prevRef.current = value;
    const t = setTimeout(() => setFlash(null), duration);
    return () => clearTimeout(t);
  }, [value, duration, direction]);
  return flash;
}

// ── Ornate gold corner brackets ────────────────────────────────────
function OrnateCorners({ color = GM.inkGold, inset = -2, len = 9, thickness = 1.5 }) {
  const arm = (pos) => {
    const base = { position: "absolute", pointerEvents: "none" };
    const h = { width: len, height: thickness, background: color, boxShadow: `0 0 4px ${color}88` };
    const v = { width: thickness, height: len, background: color, boxShadow: `0 0 4px ${color}88` };
    switch (pos) {
      case "tl": return [<div key="a" style={{ ...base, ...h, top: inset, left: inset }} />, <div key="b" style={{ ...base, ...v, top: inset, left: inset }} />];
      case "tr": return [<div key="a" style={{ ...base, ...h, top: inset, right: inset }} />, <div key="b" style={{ ...base, ...v, top: inset, right: inset }} />];
      case "bl": return [<div key="a" style={{ ...base, ...h, bottom: inset, left: inset }} />, <div key="b" style={{ ...base, ...v, bottom: inset, left: inset }} />];
      case "br": return [<div key="a" style={{ ...base, ...h, bottom: inset, right: inset }} />, <div key="b" style={{ ...base, ...v, bottom: inset, right: inset }} />];
      default: return null;
    }
  };
  return <>{arm("tl")}{arm("tr")}{arm("bl")}{arm("br")}</>;
}

// ── Ornate nameplate — hexagonal sigil + player name ───────────────
function NamePlate({ name, isMe, isActive }) {
  const A = isMe
    ? { edge: GM.inkGold, bright: GM.inkGoldBright, glow: GM.inkGold }
    : { edge: "#a83232", bright: "#e05555", glow: "#c03a3a" };
  return (
    <div style={{
      position: "relative",
      display: "inline-flex", alignItems: "center", gap: isMobile ? 5 : 9,
      padding: isMobile ? "4px 9px 4px 7px" : "7px 16px 7px 11px",
      background: `linear-gradient(135deg, ${GM.pageWarm}ee, ${GM.page}f2)`,
      border: `1.5px solid ${A.edge}`,
      borderRadius: 4,
      boxShadow: isActive
        ? `0 0 18px ${A.glow}55, inset 0 0 16px ${A.edge}14, 0 2px 8px rgba(0,0,0,0.6)`
        : `inset 0 0 12px ${A.edge}0a, 0 2px 8px rgba(0,0,0,0.6)`,
      transition: "box-shadow 0.4s",
      backdropFilter: "blur(8px)",
      minWidth: 0,
    }}>
      <OrnateCorners color={A.bright} inset={2} len={isMobile ? 6 : 8} />
      <svg width={isMobile ? 18 : 26} height={isMobile ? 19 : 28} viewBox="0 0 26 28"
        style={{ flexShrink: 0, filter: `drop-shadow(0 0 5px ${A.glow}66)` }}>
        <polygon points="13,2 23,8 23,20 13,26 3,20 3,8" fill="none" stroke={A.edge} strokeWidth="1.3" />
        <polygon points="13,7 18.5,10.5 18.5,17.5 13,21 7.5,17.5 7.5,10.5" fill={`${A.edge}1f`} stroke={A.bright} strokeWidth="0.9" />
        <path d="M 13 10.5 L 16 13.5 L 13 17.5 L 10 13.5 Z" fill={A.bright} opacity="0.92" />
      </svg>
      <span style={{
        fontSize: isMobile ? 10 : 14, fontWeight: 900, fontStyle: "italic",
        letterSpacing: isMobile ? 1.5 : 3, fontFamily: GM.font,
        color: GM.vellum, textShadow: `0 0 10px ${A.glow}66`,
        textTransform: "uppercase", whiteSpace: "nowrap",
        overflow: "hidden", textOverflow: "ellipsis", maxWidth: isMobile ? 96 : 170,
      }}>{name || "???"}</span>
    </div>
  );
}

// ── Framed timer — lit only on that player's turn ──────────────────
function FramedTimer({ secs, isActive, urgent, isMe = true }) {
  const edge = urgent ? "#dc2626" : isMe ? GM.inkGold : "#a83232";
  const col = urgent ? "#dc2626" : isMe ? GM.inkGoldBright : "#e05555";
  return (
    <div style={{
      position: "relative",
      padding: isMobile ? "3px 10px" : "5px 20px",
      background: `linear-gradient(135deg, ${GM.pageWarm}ee, ${GM.page}f2)`,
      border: `1.5px solid ${edge}`,
      borderRadius: 4,
      boxShadow: isActive
        ? `0 0 16px ${col}44, inset 0 0 12px ${col}14, 0 2px 8px rgba(0,0,0,0.6)`
        : `inset 0 0 10px ${edge}0a, 0 2px 8px rgba(0,0,0,0.6)`,
      opacity: isActive ? 1 : 0.5,
      transition: "all 0.4s",
      backdropFilter: "blur(8px)",
      flexShrink: 0, textAlign: "center",
    }}>
      <OrnateCorners color={urgent ? "#ff6b6b" : isMe ? GM.inkGoldBright : "#e05555"} inset={2} len={isMobile ? 6 : 7} />
      <span style={{
        fontSize: isMobile ? 14 : 24, fontWeight: 900, fontStyle: "italic",
        letterSpacing: isMobile ? 1 : 2, fontFamily: GM.font, color: col,
        textShadow: `0 0 12px ${col}66`,
      }}>{formatTime(secs)}</span>
    </div>
  );
}

// ── Crystalline Anima heart ────────────────────────────────────────
function AnimaHeart({ value, isMe, isActive }) {
  const flash = useFlashOnChange(value, { duration: 600 });
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: isMobile ? 5 : 10,
      opacity: isActive ? 1 : 0.82, transition: "opacity 0.3s",
    }}>
      <svg width={isMobile ? 42 : 78} height={isMobile ? 42 : 78} viewBox="0 0 80 80"
        style={{ flexShrink: 0, filter: "drop-shadow(0 0 14px #c0202088) drop-shadow(0 0 6px #ff334466)" }}>
        <defs>
          <linearGradient id={`sf3dAnimaG-${isMe ? "me" : "op"}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ff5a6e" />
            <stop offset="45%" stopColor="#d31030" />
            <stop offset="100%" stopColor="#6e0014" />
          </linearGradient>
        </defs>
        <path d="M40 72 L10 40 Q4 30 12 22 Q22 12 32 22 L40 30 L48 22 Q58 12 68 22 Q76 30 70 40 Z"
          fill={`url(#sf3dAnimaG-${isMe ? "me" : "op"})`} stroke="#ff8294" strokeWidth="1" />
        <g stroke="#ffb3c0" strokeWidth="0.7" opacity="0.55" fill="none">
          <path d="M40 30 L22 40 L40 64 Z" />
          <path d="M40 30 L58 40 L40 64 Z" />
          <path d="M22 40 L14 34 M58 40 L66 34 M40 30 L40 22" />
          <path d="M22 40 L32 50 M58 40 L48 50" />
        </g>
        <path d="M30 26 Q26 24 24 28" stroke="#ffd0d8" strokeWidth="1.4" opacity="0.7" fill="none" strokeLinecap="round" />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 0.95 }}>
        <span style={{ fontSize: isMobile ? 10 : 17, fontWeight: 900, fontStyle: "italic", letterSpacing: isMobile ? 1.5 : 3, fontFamily: GM.font, color: SD.red, textShadow: `0 0 10px ${SD.red}66`, textTransform: "uppercase" }}>Anima</span>
        <span className={flash ? "sf-hp-flash" : ""} style={{ fontSize: isMobile ? 22 : 40, fontWeight: 900, fontFamily: GM.font, color: SD.red, textShadow: `0 0 16px ${SD.red}88`, display: "inline-block", transformOrigin: "left center" }}>{value}</span>
      </div>
    </div>
  );
}

// ── Khao vortex ────────────────────────────────────────────────────
function KhaoVortex({ khao, maxKhao, isMe, isActive }) {
  const flash = useFlashOnChange(`${khao}/${maxKhao}`, { duration: 600 });
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: isMobile ? 5 : 10, flexDirection: "row-reverse",
      opacity: isActive ? 1 : 0.82, transition: "opacity 0.3s",
    }}>
      <div style={{
        width: isMobile ? 42 : 84, height: isMobile ? 42 : 84, borderRadius: "50%",
        flexShrink: 0, position: "relative", overflow: "hidden",
        background: "radial-gradient(circle at 50% 50%, #1a0533 0%, #3b0764 28%, #6d28d9 58%, #2a0a4a 100%)",
        boxShadow: `0 0 22px #6d28d988, inset 0 0 20px #1a0533, 0 0 0 1.5px #9333ea66`,
      }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "conic-gradient(from 0deg, transparent, #a855f7aa, transparent 40%, #7c3aed99, transparent 75%)", animation: "sf-khaoSpin 7s linear infinite" }} />
        <div style={{ position: "absolute", inset: "30%", borderRadius: "50%", background: "radial-gradient(circle, #0a0014 0%, #2a0a4a 100%)", boxShadow: "inset 0 0 12px #000" }} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 0.95, alignItems: "flex-end" }}>
        <span style={{ fontSize: isMobile ? 10 : 17, fontWeight: 900, fontStyle: "italic", letterSpacing: isMobile ? 1.5 : 3, fontFamily: GM.font, color: SD.gold, textShadow: `0 0 10px ${SD.gold}66`, textTransform: "uppercase" }}>Khao</span>
        <span className={flash ? "sf-khao-flash" : ""} style={{ fontSize: isMobile ? 19 : 34, fontWeight: 900, fontFamily: GM.font, color: SD.gold, textShadow: `0 0 16px ${SD.gold}88`, display: "inline-block", transformOrigin: "right center" }}>{khao}/{maxKhao}</span>
      </div>
    </div>
  );
}

// ── THE LUX / GRIM TILES ARE GONE (July 25 — NO REDUNDANCY) ────────
// They were the "always-reachable twins" for the 3D Grimoire and Luxmoire
// piles. But the piles ARE reachable: they stand in the world at the seat,
// they carry their own live count badges, and they take the same click
// through the same gate. Two surfaces for one action is two things to keep
// in sync and one thing too many on screen. The world owns the draw now.
// (If a camera preset ever hides the piles, fix the preset, not the HUD.)

// ═══════════════════════════════════════════════════════════════════
// THE OVERLAY
// ═══════════════════════════════════════════════════════════════════
export default function FieldChrome3D({
  me, opponent, myTurn = false, sharedTimer = null,
}) {
  useEffect(() => { ensureChromeKeyframes(); }, []);

  const myAnima = me?.anima ?? 30;
  const oppAnima = opponent?.anima ?? 30;

  // ── IMPACT — a struck seat flashes the whole screen once. Direction-
  //    aware so a heal reads green and a hit reads red. The 3D board's
  //    world-shake is fired from Shatterfield3D's own detector; this is
  //    the light that comes with the thunder.
  const myHit = useFlashOnChange(myAnima, { direction: true, duration: 520 });
  const oppHit = useFlashOnChange(oppAnima, { direction: true, duration: 520 });
  const activeFlash = myHit || oppHit;

  // Danger veil — my Anima in the red. Same threshold language the 2D
  // field uses for its dangerBreathe class.
  const danger = myAnima <= 10;
  const dire = myAnima <= 5;

  const oppActive = !myTurn;
  const urgent = sharedTimer != null && sharedTimer <= 10;

  const col = { display: "flex", flexDirection: "column", gap: isMobile ? 5 : 8 };

  return (
    <>
      {/* ══ VEILS — full-bleed, never eat a click ══ */}
      {danger && (
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none", zIndex: 6,
          background: `radial-gradient(ellipse at 50% 108%, ${dire ? "#dc2626" : "#8b2020"}55 0%, transparent 58%)`,
          animation: `sf3dc-dangerVeil ${dire ? 1.1 : 2.2}s ease-in-out infinite`,
        }} />
      )}
      {activeFlash && (
        <div key={`${myAnima}|${oppAnima}`} style={{
          position: "absolute", inset: 0, pointerEvents: "none", zIndex: 7,
          background: activeFlash === "down"
            ? "radial-gradient(ellipse at 50% 50%, transparent 32%, #dc262688 100%)"
            : "radial-gradient(ellipse at 50% 50%, transparent 32%, #4ade8066 100%)",
          animation: `${activeFlash === "down" ? "sf3dc-hitFlash" : "sf3dc-healFlash"} 0.52s ease-out forwards`,
        }} />
      )}

      {/* ══ THEIR SEAT ══ */}
      <div style={{
        position: "absolute", top: isMobile ? 44 : 58, left: isMobile ? 8 : 16,
        zIndex: 12, pointerEvents: "none",
        animation: "sf3dc-plateRise 0.5s ease-out both",
      }}>
        <AnimaHeart value={oppAnima} isMe={false} isActive={oppActive} />
      </div>

      <div style={{
        position: "absolute", top: isMobile ? 44 : 52, right: isMobile ? 8 : 16,
        zIndex: 12, pointerEvents: "none", alignItems: "flex-end",
        animation: "sf3dc-plateRise 0.5s ease-out both", ...col,
      }}>
        <NamePlate name={opponent?.name || "Adversary"} isMe={false} isActive={oppActive} />
        {sharedTimer != null && (
          <FramedTimer secs={sharedTimer} isActive={oppActive} urgent={oppActive && urgent} isMe={false} />
        )}
        <div style={{ marginTop: isMobile ? 2 : 6 }}>
          <KhaoVortex khao={opponent?.khao ?? 0} maxKhao={opponent?.maxKhao ?? 3}
            isMe={false} isActive={oppActive} />
        </div>
        <div style={{
          fontFamily: GM.font, fontSize: 10, letterSpacing: 2, color: GM.vellumFaint,
          background: "rgba(7,4,16,0.8)", border: `1px solid ${GM.inkGold}22`,
          borderRadius: 4, padding: "2px 8px", backdropFilter: "blur(6px)",
        }}>⬒ {(opponent?.hand || []).length} IN HAND</div>
      </div>

      {/* ══ MY SEAT ══ */}
      <div style={{
        position: "absolute", left: isMobile ? 8 : 16, bottom: isMobile ? 108 : 196,
        zIndex: 12, pointerEvents: "none",
        animation: "sf3dc-plateRise 0.5s ease-out both", ...col,
      }}>
        <NamePlate name={me?.name || "You"} isMe isActive={myTurn} />
        {sharedTimer != null && (
          <FramedTimer secs={sharedTimer} isActive={myTurn} urgent={myTurn && urgent} isMe />
        )}
      </div>

      <div style={{
        position: "absolute", left: isMobile ? 8 : 16, bottom: isMobile ? 56 : 106,
        zIndex: 12, pointerEvents: "none",
        animation: "sf3dc-plateRise 0.5s ease-out both",
      }}>
        <AnimaHeart value={myAnima} isMe isActive={myTurn} />
      </div>

      <div style={{
        position: "absolute", right: isMobile ? 8 : 16, bottom: isMobile ? 8 : 16,
        zIndex: 12, pointerEvents: "none",
        display: "flex", flexDirection: "column", alignItems: "flex-end", gap: isMobile ? 8 : 14,
        animation: "sf3dc-plateRise 0.5s ease-out both",
      }}>
        {/* Draw tiles deleted — the Grimoire / Luxmoire piles in the world
            are the one and only draw surface. This corner is chrome-only
            again: NOTHING in this file is interactive now. */}
        <KhaoVortex khao={me?.khao ?? 3} maxKhao={me?.maxKhao ?? 3} isMe isActive={myTurn} />
      </div>
    </>
  );
}
